import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Plus, Pencil, Trash2, BookOpen, Save, X } from "lucide-react";
import { toast } from "sonner";
import VoiceInputButton from "@/components/VoiceInputButton";

const CONFIDENCE_BG = {
  green: "bg-green-50 border-green-200",
  yellow: "bg-yellow-50 border-yellow-200",
  red: "bg-red-50 border-red-200",
};

export default function GregoryAI() {
  const { apiCall } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: "", category: "Geral", tags: "", content: "" });
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiCall("get", "/knowledge");
      setArticles(res.data || []);
    } catch (e) {
      toast.error("Erro ao carregar base");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Busca com debounce
  useEffect(() => {
    if (!q.trim()) {
      setSearchResult(null);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await apiCall("get", `/knowledge/search?q=${encodeURIComponent(q)}`);
        setSearchResult(res.data);
      } catch (e) {
        setSearchResult(null);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const categories = useMemo(() => {
    const s = new Set(articles.map((a) => a.category || "Geral"));
    return ["Todos", ...Array.from(s).sort()];
  }, [articles]);
  const [categoryFilter, setCategoryFilter] = useState("Todos");

  const visibleArticles = useMemo(() => {
    if (categoryFilter === "Todos") return articles;
    return articles.filter((a) => (a.category || "Geral") === categoryFilter);
  }, [articles, categoryFilter]);

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Título e conteúdo obrigatórios");
      return;
    }
    const tagsArr = form.tags.split(",").map((t) => t.trim()).filter(Boolean);
    try {
      if (editingId) {
        await apiCall("put", `/knowledge/${editingId}`, { ...form, tags: tagsArr });
        toast.success("Artigo atualizado");
      } else {
        await apiCall("post", "/knowledge", { ...form, tags: tagsArr });
        toast.success("Artigo criado");
      }
      setOpen(false);
      setEditingId(null);
      setForm({ title: "", category: "Geral", tags: "", content: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    }
  };

  const handleEdit = (a) => {
    setEditingId(a.article_id);
    setForm({
      title: a.title,
      category: a.category || "Geral",
      tags: (a.tags || []).join(", "),
      content: a.content || "",
    });
    setOpen(true);
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Excluir "${a.title}"?`)) return;
    try {
      await apiCall("delete", `/knowledge/${a.article_id}`);
      toast.success("Removido");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    }
  };

  return (
    <div className="space-y-4" data-testid="gregory-ai-page">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Técnico Capilar</div>
          <h1 className="text-2xl font-light tracking-tight flex items-center gap-2">
            <BookOpen className="h-5 w-5" strokeWidth={1.5} />
            Gregory AI
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Base de protocolos, colorimetria, técnicas e diagnósticos. Busca inteligente sem custo de IA.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditingId(null);
            setForm({ title: "", category: "Geral", tags: "", content: "" });
            setOpen(true);
          }}
          data-testid="new-knowledge-btn"
        >
          <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} />
          Novo
        </Button>
      </div>

      <Card className="p-3 flex flex-col sm:flex-row gap-2 items-center">
        <div className="flex-1 w-full flex items-center gap-1">
          <Search className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          <Input
            placeholder="Pergunte ao Gregory: 'cabelo elástico após descoloração'…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="knowledge-search"
            className="border-none focus-visible:ring-0"
          />
          <VoiceInputButton testId="knowledge-search-voice" onTranscript={(t) => setQ(t)} />
        </div>
        {searching && <span className="text-[10px] text-muted-foreground">Buscando…</span>}
      </Card>

      {/* Resultados da busca */}
      {searchResult && searchResult.results.length > 0 && (
        <div className="space-y-2" data-testid="search-results">
          <div className={`p-3 rounded-md border ${CONFIDENCE_BG[searchResult.top_confidence.level]}`}>
            <div className="text-xs font-medium" data-testid="confidence-top">
              {searchResult.top_confidence.icon} {searchResult.top_confidence.label} ({searchResult.top_confidence.pct}%)
            </div>
          </div>
          {searchResult.results.map((r) => (
            <Card key={r.article.article_id} className="p-3" data-testid={`search-result-${r.article.article_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    {r.article.category} · {r.confidence.icon} {r.confidence.pct}%
                  </div>
                  <h3 className="text-base font-medium">{r.article.title}</h3>
                  <p className="text-xs text-muted-foreground whitespace-pre-wrap mt-2">{r.article.content}</p>
                  {(r.article.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {r.article.tags.map((t) => (
                        <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary/70">
                          #{t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <Button size="icon" variant="ghost" onClick={() => handleEdit(r.article)}>
                    <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => handleDelete(r.article)}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {searchResult && searchResult.results.length === 0 && (
        <Card className="p-4 text-center" data-testid="search-empty">
          <div className="text-sm">🔴 Não encontrei protocolo adequado.</div>
          <p className="text-xs text-muted-foreground mt-1">
            Você pode cadastrar este protocolo clicando em "Novo" ou perguntar ao assistente IA (custo aplicável).
          </p>
        </Card>
      )}

      {/* Lista geral */}
      {!searchResult && (
        <>
          <div className="flex gap-1 flex-wrap">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCategoryFilter(c)}
                className={`text-xs px-2.5 py-1 rounded-full border transition ${
                  categoryFilter === c ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"
                }`}
                data-testid={`cat-filter-${c}`}
              >
                {c}
              </button>
            ))}
          </div>

          {loading ? (
            <Card className="p-6 text-center text-muted-foreground">Carregando…</Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="articles-grid">
              {visibleArticles.map((a) => (
                <Card key={a.article_id} className="p-3 hover:shadow-md transition" data-testid={`article-${a.article_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{a.category}</div>
                      <h3 className="font-medium truncate">{a.title}</h3>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="icon" variant="ghost" onClick={() => handleEdit(a)} className="h-7 w-7">
                        <Pencil className="h-3 w-3" strokeWidth={1.5} />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => handleDelete(a)} className="h-7 w-7">
                        <Trash2 className="h-3 w-3 text-destructive" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-3 mt-2">{a.content}</p>
                  {(a.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {a.tags.slice(0, 6).map((t) => (
                        <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-full bg-secondary/70">
                          #{t}
                        </span>
                      ))}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="knowledge-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-light tracking-tight">
              {editingId ? "Editar protocolo" : "Novo protocolo"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Título</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="knowledge-title" />
              </div>
              <div>
                <Label className="text-xs">Categoria</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Diagnóstico, Química, Colorimetria…" data-testid="knowledge-category" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Tags (separadas por vírgula)</Label>
              <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="elástico, reconstrução, danificado" data-testid="knowledge-tags" />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-2">
                Conteúdo
                <VoiceInputButton size="sm" testId="knowledge-content-voice" onTranscript={(t) => setForm((f) => ({ ...f, content: (f.content || "") + " " + t }))} />
              </Label>
              <Textarea
                rows={10}
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="Sintomas, protocolo passo a passo, dicas…"
                data-testid="knowledge-content"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setOpen(false)}><X className="h-4 w-4 mr-1" strokeWidth={1.5} />Cancelar</Button>
              <Button onClick={handleSave} data-testid="knowledge-save"><Save className="h-4 w-4 mr-1" strokeWidth={1.5} />Salvar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
